/**
 * Clean Hybrid Analytics Service for CyclingBuddy
 * 
 * Architecture:
 * - Plausible: Basic pageviews only (no custom events/goals)
 * - Normalized Analytics: All business logic and detailed tracking
 * 
 * This separation provides:
 * - Simple overview metrics via Plausible dashboard
 * - Detailed business insights via normalized database analytics
 * - Privacy-compliant anonymous tracking
 * - No complex Plausible goal setup required
 */

// Types for normalized analytics events (privacy-compliant)
export interface RouteCalculatedEvent {
  routeSessionId: string | null; // Only for local session correlation, not stored
  routeStorageId: string | null; // Only for local correlation, not stored
  routePointsCount: number;
  calculationTimeMs: number;
  calculationStatus: 'success' | 'error' | 'cancelled';
  bikeProfile: string;
  routeDistanceM: number; // Distance in meters
  elevationGainM: number;
}

export interface GPXUploadedEvent {
  filename: string; // Only used locally, not stored in analytics
  fileSizeBytes: number;
  pointsCount: number;
  routeStorageId?: string; // Only for local correlation, not stored
  bikeProfile?: string; // Bike profile used for processing the GPX
}

export interface GPXDownloadedEvent {
  filename: string; // Only used locally, not stored in analytics
  fileSizeBytes: number;
  pointsCount: number;
  routeStorageId?: string; // Only for local correlation, not stored
  source: 'route_export' | 'manual_download';
  bikeProfile: string; // Bike profile used for the route
}

export interface AnalyticsMetadata {
  customData?: Record<string, any>;
}

class NormalizedAnalyticsService {
  private sessionId: string;
  private supabaseUrl: string;
  private anonKey: string;
  private sessionDbId: string | null = null; // Database session ID after creation
  
  constructor() {
    // Generate a session ID for anonymous tracking
    this.sessionId = this.generateSessionId();
    // Use environment variables like the existing code
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
    this.anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvY2FsaG9zdCIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzM2NzA1NjAwLCJleHAiOjE4NTIzNjI3MDB9.a2kcrdv2Ydk4yHxB4HTkXUdFna-nt4r-OgQW-Tq8a_k';
    
    // Initialize session on startup
    this.initializeSession();
  }

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getMetadata(): AnalyticsMetadata {
    // Privacy-first: Only track non-identifying technical metadata
    const browserType = this.getBrowserType();
    const platform = this.getPlatformType();
    
    return {
      customData: {
        timestamp: Date.now(), // Only for data integrity
        browser_type: browserType,
        platform: platform,
        app_version: '1.0.0' // Static app version
      }
    };
  }

  private getBrowserType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('chrome')) return 'chrome';
    if (userAgent.includes('firefox')) return 'firefox';
    if (userAgent.includes('safari')) return 'safari';
    if (userAgent.includes('edge')) return 'edge';
    return 'other';
  }

  private getPlatformType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('mobile')) return 'mobile';
    if (userAgent.includes('tablet')) return 'tablet';
    return 'desktop';
  }

  /**
   * Initialize analytics session in database
   */
  private async initializeSession() {
    try {
      const metadata = this.getMetadata();
      
      const sessionData = {
        anonymous_session_id: this.sessionId,
        browser_type: metadata.customData?.browser_type,
        platform: metadata.customData?.platform,
        app_version: metadata.customData?.app_version,
        metadata: {
          initialized_at: metadata.customData?.timestamp
        }
      };

      const response = await fetch(`${this.supabaseUrl}/rest/v1/analytics_sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.anonKey,
          'Authorization': `Bearer ${this.anonKey}`,
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(sessionData)
      });

      if (response.ok) {
        const session = await response.json();
        this.sessionDbId = session[0]?.id;
      } 
    } catch (error) {
      console.error('[Analytics] Error initializing session:', error);
    }
  }

  /**
   * Get or create session ID in database
   */
  private async getSessionDbId(): Promise<string | null> {
    if (this.sessionDbId) {
      return this.sessionDbId;
    }

    // Try to initialize session if not done yet
    await this.initializeSession();
    return this.sessionDbId;
  }

  // ========================================
  // PLAUSIBLE ANALYTICS (Standard Tracking)
  // ========================================

  /**
   * Track page views - Plausible handles this automatically, but we can trigger custom page views
   */
  trackPageView(page: string, props?: Record<string, string | number>) {
    if (typeof window !== 'undefined' && (window as any).plausible) {
      (window as any).plausible('pageview', {
        props: {
          page,
          ...props
        }
      });
    }
  }

  // ========================================
  // NORMALIZED ANALYTICS (Custom Events)
  // ========================================

  /**
   * Track detailed route calculation events (normalized structure)
   */
  async trackRouteCalculated(eventData: RouteCalculatedEvent) {
    try {
      const sessionDbId = await this.getSessionDbId();
      if (!sessionDbId) {
        console.error('[Analytics] No session ID available for route calculation tracking');
        return;
      }

      // Privacy-compliant analytics event - stored in dedicated table
      const routeCalculationData = {
        session_id: sessionDbId,
        route_session_id: eventData.routeSessionId, // For tracking route iteration patterns
        route_storage_id: eventData.routeStorageId, // For tracking edits to saved routes
        route_points_count: eventData.routePointsCount,
        calculation_time_ms: eventData.calculationTimeMs,
        calculation_status: eventData.calculationStatus,
        bike_profile: eventData.bikeProfile,
        route_distance_m: eventData.routeDistanceM,
        elevation_gain_m: eventData.elevationGainM
      };

      const response = await fetch(`${this.supabaseUrl}/rest/v1/analytics_route_calculations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.anonKey,
          'Authorization': `Bearer ${this.anonKey}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify(routeCalculationData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Analytics] Failed to track route calculation:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          data: routeCalculationData
        });
      }

      // Plausible only handles basic pageviews - business logic tracked in normalized analytics

    } catch (error) {
      console.error('[Analytics] Error tracking route calculation:', error);
    }
  }

  /**
   * Track GPX upload events (normalized structure)
   */
  async trackGPXUploaded(eventData: GPXUploadedEvent) {
    try {
      const sessionDbId = await this.getSessionDbId();
      if (!sessionDbId) {
        console.error('[Analytics] No session ID available for GPX upload tracking');
        return;
      }

      // Privacy-compliant analytics event - stored in dedicated table
      const gpxOperationData = {
        session_id: sessionDbId,
        operation_type: 'upload',
        file_size_bytes: eventData.fileSizeBytes,
        file_points_count: eventData.pointsCount,
        route_storage_id: eventData.routeStorageId || null,
        bike_profile: eventData.bikeProfile || null
      };

      const response = await fetch(`${this.supabaseUrl}/rest/v1/analytics_gpx_operations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.anonKey,
          'Authorization': `Bearer ${this.anonKey}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify(gpxOperationData)
      });

      if (!response.ok) {
        console.error('[Analytics] Failed to track GPX upload:', response.statusText);
      }

      // Plausible only handles basic pageviews - business logic tracked in normalized analytics

    } catch (error) {
      console.error('[Analytics] Error tracking GPX upload:', error);
    }
  }

  /**
   * Track GPX download events (normalized structure)
   */
  async trackGPXDownloaded(eventData: GPXDownloadedEvent) {
    try {
      const sessionDbId = await this.getSessionDbId();
      if (!sessionDbId) {
        console.error('[Analytics] No session ID available for GPX download tracking');
        return;
      }

      // Privacy-compliant analytics event - stored in dedicated table
      const gpxOperationData = {
        session_id: sessionDbId,
        operation_type: eventData.source === 'route_export' ? 'export' : 'download',
        file_size_bytes: eventData.fileSizeBytes,
        file_points_count: eventData.pointsCount,
        route_storage_id: eventData.routeStorageId || null,
        bike_profile: eventData.bikeProfile
      };

      const response = await fetch(`${this.supabaseUrl}/rest/v1/analytics_gpx_operations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.anonKey,
          'Authorization': `Bearer ${this.anonKey}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify(gpxOperationData)
      });

      if (!response.ok) {
        console.error('[Analytics] Failed to track GPX download:', response.statusText);
      } 

      // Plausible only handles basic pageviews - business logic tracked in normalized analytics

    } catch (error) {
      console.error('[Analytics] Error tracking GPX download:', error);
    }
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  /**
   * Get analytics query methods for dashboard/reporting (normalized)
   */
  async getRouteCalculationStats(dateRange?: { from: Date; to: Date }) {
    try {
      let url = `${this.supabaseUrl}/rest/v1/analytics_route_calculations?select=*,session:analytics_sessions(browser_type,platform)`;
      
      if (dateRange) {
        const fromIso = dateRange.from.toISOString();
        const toIso = dateRange.to.toISOString();
        url += `&created_at=gte.${fromIso}&created_at=lte.${toIso}`;
      }

      const response = await fetch(url, {
        headers: {
          'apikey': this.anonKey,
          'Authorization': `Bearer ${this.anonKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('[Analytics] Failed to fetch route calculation stats:', response.statusText);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[Analytics] Error fetching route calculation stats:', error);
      return null;
    }
  }

  /**
   * Get GPX usage stats (normalized)
   */
  async getGPXUsageStats(dateRange?: { from: Date; to: Date }) {
    try {
      let url = `${this.supabaseUrl}/rest/v1/analytics_gpx_operations?select=*,session:analytics_sessions(browser_type,platform)`;
      
      if (dateRange) {
        const fromIso = dateRange.from.toISOString();
        const toIso = dateRange.to.toISOString();
        url += `&created_at=gte.${fromIso}&created_at=lte.${toIso}`;
      }

      const response = await fetch(url, {
        headers: {
          'apikey': this.anonKey,
          'Authorization': `Bearer ${this.anonKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('[Analytics] Failed to fetch GPX usage stats:', response.statusText);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[Analytics] Error fetching GPX usage stats:', error);
      return null;
    }
  }

  // ========================================
  // BACKWARD COMPATIBILITY METHODS (No-op stubs - Use normalized analytics only)
  // ========================================

}

// Export singleton instance
export const AnalyticsService = new NormalizedAnalyticsService();

// Expose analytics service to window for debugging
if (typeof window !== 'undefined') {
  (window as any).CyclingBuddyAnalytics = AnalyticsService;
}

export default AnalyticsService;
